export interface JustifiedLayoutInput {
  width: number;
  height: number;
}

export interface JustifiedLayoutCell {
  width: number;
  height: number;
}

export interface JustifiedLayoutRow {
  cells: JustifiedLayoutCell[];
  height: number;
  /** Start index of this row's first cell in the original input array. */
  startIndex: number;
}

export interface JustifiedLayoutOptions {
  containerWidth: number;
  targetRowHeight: number;
  gap: number;
}

/**
 * Pack items into rows that span the full container width at a target row
 * height (Google Images / Flickr style "justified rows" gallery).
 *
 * Greedily adds items to a row, recomputing the row height that would make
 * the row span containerWidth exactly. Stops when that height drops at or
 * below the target. The last partial row keeps items at the target row
 * height with their natural aspect ratio (left-aligned, no stretch) so a
 * single trailing image doesn't blow up to fill the whole width.
 */
export function computeJustifiedLayout(
  items: ReadonlyArray<JustifiedLayoutInput>,
  options: JustifiedLayoutOptions,
): JustifiedLayoutRow[] {
  const { containerWidth, targetRowHeight, gap } = options;
  if (items.length === 0 || containerWidth <= 0 || targetRowHeight <= 0) return [];

  const rows: JustifiedLayoutRow[] = [];
  let cursor = 0;

  while (cursor < items.length) {
    let end = cursor + 1;
    let rowHeight = computeRowHeight(items, cursor, end, containerWidth, gap);

    while (end < items.length && rowHeight > targetRowHeight) {
      end += 1;
      rowHeight = computeRowHeight(items, cursor, end, containerWidth, gap);
    }

    const isLastRowUnderflow = end === items.length && rowHeight > targetRowHeight;
    const finalHeight = isLastRowUnderflow ? targetRowHeight : rowHeight;

    const slice = items.slice(cursor, end);
    const cells: JustifiedLayoutCell[] = slice.map((it) => ({
      width: finalHeight * (it.width / it.height),
      height: finalHeight,
    }));

    rows.push({ cells, height: finalHeight, startIndex: cursor });
    cursor = end;
  }

  return rows;
}

function computeRowHeight(
  items: ReadonlyArray<JustifiedLayoutInput>,
  start: number,
  end: number,
  containerWidth: number,
  gap: number,
): number {
  const slice = items.slice(start, end);
  if (slice.length === 0) return 0;
  let ratioSum = 0;
  for (const it of slice) {
    if (it.height <= 0) continue;
    ratioSum += it.width / it.height;
  }
  if (ratioSum <= 0) return 0;
  const totalGap = gap * (slice.length - 1);
  return (containerWidth - totalGap) / ratioSum;
}
